<template>
  <div v-if="!isLoading" class="red-calendar">
    <CommonBackdrop v-model:isOpen="isBackdropOpen">
      <template #title>
        <div class="backdrop-title" v-html="backdropTitleText"></div>
      </template>
      <div class="backdrop-content">
        <Details
          :data="tileDetails"
          :currentPayment="currentPayment"
          :hasAlert="hasForPaymentAlert"
          :historyItems="historyItems"
        />
      </div>
    </CommonBackdrop>
    <div v-if="areBillsEmpty" class="red-calendar__empty">
      <div class="red-calendar__empty_img"></div>
      <div class="red-calendar__empty_text">{{ $t`У Вас нет счетов` }}</div>
    </div>
    <template v-else>
      <template v-for="(item, index) in bills" :key="item">
        <div
          v-if="isYearVisible(bills[index - 1], item)"
          class="red-calendar__year"
        >
          {{ getYear(item.plan_date) }}
        </div>
        <PaymentCalendarTile
          :tile="item"
          :isFirstPrepayment="firstPrepaymentDate === item.open_date"
          :isCurrentPayment="
            currentPayment && currentPayment.plan_date === item.plan_date
          "
          class="red-calendar__tile"
          @click="openCalendarBackdrop(item)"
        />
      </template>
    </template>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { getDateText } from "@/utils/date";
import { fetchBillsCalendar, fetchInvoices } from "@/api/calendar";
import { mapGetters, mapActions } from "vuex";
import PaymentCalendarTile from "@/components/bills-calendar/PaymentCalendarTile.vue";
import CommonBackdrop from "@/components/shared/backdrop/CommonBackdrop.vue";
import Details from "@/components/bills-calendar/PaymentCalendarItemDetails.vue";

export default {
  name: "RedCalendar",
  components: {
    Details,
    CommonBackdrop,
    PaymentCalendarTile,
  },
  data() {
    return {
      isLoading: true,
      isBackdropOpen: false,
      bills: [],
      tileDetails: null,
      historyItems: [],
      currentPayment: null,
      firstPrepaymentDate: "",
    };
  },
  async created() {
    if (!this.redAccount) {
      await this.fetchBundle();
    }
    await this.GetCalendar();
    this.isLoading = false;
    const tileDate = this.$route.query.date;
    this.showTileByDate(tileDate);
  },
  computed: {
    ...mapGetters("bundle", ["getRedAccounts"]),
    billTypes() {
      return {
        OVERDUE: {
          typeName: "overdue",
          isPresent: false,
        },
        WAS_OVERDUE: {
          typeName: "past",
          isPresent: false,
        },
        PAID: {
          typeName: "paid",
          isPresent: false,
        },
        UNPAID: {
          typeName: "unpaid",
          isPresent: false,
        },
        FOR_PAYMENT: {
          typeName: "current",
          isPresent: false,
        },
        PRE_PAYMENT: {
          typeName: "future",
          isPresent: false,
        },
        NO_PAYMENT: {
          typeName: "empty",
          isPresent: false,
        },
      };
    },
    backdropTitleText() {
      if (!this.tileDetails) {
        return "";
      }

      const planDate = dayjs(this.tileDetails.plan_date, "YYYY-MM-DD");
      if (this.tileDetails.status === "PRE_PAYMENT") {
        return this.$l10nformat(this.$t`Предварительный счет </br> за #DATE#`, {
          DATE: getDateText(this.tileDetails.plan_date, "dative", "MMMM"),
        });
      }

      if (
        this.tileDetails.status === "FOR_PAYMENT" ||
        this.tileDetails.status === "PAID"
      ) {
        return this.$l10nformat(this.$t`Счет за #DATE#`, {
          DATE: planDate.format("MMMM"),
        });
      }

      if (this.tileDetails.status === "WAS_OVERDUE") {
        return this.$l10nformat(this.$t`Просроченный счет за #DATE#`, {
          DATE: planDate.format("MMMM"),
        });
      }

      return "";
    },
    hasForPaymentAlert() {
      return (
        this.billTypes["FOR_PAYMENT"].isPresent &&
        this.billTypes["WAS_OVERDUE"].isPresent
      );
    },
    redAccount() {
      if (this.getRedAccounts && this.getRedAccounts.length) {
        return this.getRedAccounts[0];
      }
      return null;
    },
    areBillsEmpty() {
      if (this.redAccount && !this.bills?.length) {
        const redProduct = this.redAccount;
        return redProduct.own_amount == 0 || !redProduct.accounting_date;
      }
      return false;
    },
  },
  methods: {
    ...mapActions("bundle", ["fetchBundle"]),
    ...mapActions("system", ["showAlert"]),
    getYear(dateISOString) {
      return dateISOString.substring(0, 4);
    },
    showTileByDate(date) {
      if (!date) return;

      const tile = this.bills.find(
        (bill) => date === this.$getFormattedDate(bill.plan_date, "DD.MM.YYYY")
      );
      this.openCalendarBackdrop(tile);
    },
    isYearVisible(item1, item2) {
      if (!item1 || !item2) {
        return true;
      }
      return this.getYear(item1.plan_date) !== this.getYear(item2.plan_date);
    },
    async GetCalendar() {
      if (!this.redAccount) {
        return;
      }

      const response = await fetchBillsCalendar();
      response.forEach((item) => {
        if (!this.firstPrepaymentDate && item.status === "PRE_PAYMENT") {
          this.firstPrepaymentDate = item.open_date;
        }
        this.billTypes[item.status].isPresent = true;
        if (
          item.status !== "NO_PAYMENT" &&
          item.status !== "PAID" &&
          !this.currentPayment
        ) {
          this.currentPayment = item;
        }
      });

      this.bills = response;

      this.$logAnalyticEvent("mybank_red_calendar", {
        started_from: "mybank",
        name: "mybank_events",
        came_from:
          this.$route.query.src === "purchases"
            ? "purchases"
            : this.$route.query.src === "prepayment"
            ? "prepayment"
            : "info",
        label: "Пользователь зашел в раздел «Календарь счетов»",
        has_current_bill: this.billTypes["FOR_PAYMENT"].isPresent.toString(),
        has_future_bill: this.billTypes["PRE_PAYMENT"].isPresent.toString(),
        has_overdue_bill: this.billTypes["WAS_OVERDUE"].isPresent.toString(),
        has_paid_bill: this.billTypes["PAID"].isPresent.toString(),
      });
    },
    async openCalendarBackdrop(tile) {
      if (!tile || tile.status === "NO_PAYMENT") {
        return;
      }

      this.tileDetails = tile;

      await this.getHistoryItems(tile);
      this.isBackdropOpen = true;

      const status = this.tileDetails.status;
      this.$logAnalyticEvent("mybank_red_calendar_bill_details", {
        started_from: "mybank",
        name: "mybank_events",
        label: "Пользователь нажал на один из счетов",
        bill_type: this.billTypes[status].typeName || "default",
      });
    },
    async getHistoryItems(tile) {
      const invoicePayload = {
        simpleId: this.getRedAccounts[0].simple_id,
        billNum: null,
        date: null,
        type: "ForCalendar",
      };
      if (tile.status === "PRE_PAYMENT") {
        invoicePayload.date = tile.plan_date;
        invoicePayload.type = "ForPrepayment";
      } else {
        invoicePayload.billNum = tile.num_acc;
      }
      const response = await fetchInvoices(invoicePayload);
      this.historyItems = response.shops_list;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/colors.scss";

.backdrop-title::first-letter {
  text-transform: capitalize;
}

.red-calendar {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  font-family: "Roboto-Regular";

  &__empty {
    font-family: "Roboto-Medium";
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 100vw;
    height: 450px;

    &_img {
      width: 70px;
      height: 90px;
      margin-bottom: 20px;
      background-image: url(~@/assets/icon_emptyBills.png);
      background-repeat: no-repeat;
      background-position: center;
      background-size: 70px auto;
    }

    &_text {
      font-size: 24px;
      color: #4a4a4a;
    }
  }

  &__tile {
    flex: 0 0 calc(100% / 3);
  }

  &__year {
    flex-basis: 100%;
    color: #4a4a4a;
    text-align: left;
    padding: 12px 15px;
  }
}
</style>
